import * as React from 'react';

import {
  CalendarDate,
  getLocalTimeZone,
  today,
  toZoned,
} from '@internationalized/date';
import {
  enumBookingStatus,
  enumInvoiceEntityReferenceType,
  enumInvoiceStatus,
  enumLoadStatus,
  enumPaymentTerm,
  enumSalesLoadLineItemPhase,
  LoadStatus,
  Weight,
} from '@rematter/frontend-lib-api-client';
import { useLoadDetailMenuItems } from '@rematter/modules/trade/hooks/useLoadDetailMenuItems';
import { Button, Menu, Tooltip } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { MoreHorizontal } from 'lucide-react';

import {
  $createInvoiceFromReconciliation,
  $markAsReceived,
  $markAsReconciled,
  $markAsShippedAndUpdateLoad,
  $markAsVoided,
  $revertStatus,
  $updateInvoiceLineItems,
} from '../../_state/loadDetail.state';
import { $loadForm, $tagToLineItemMap } from '../../_state/loadForm.state';
import {
  $deductionTypes,
  $materialsReconciliationForm,
  $setInvoiceId,
} from '../../_state/loadReconciliation.state';
import { useTranslations } from '../../i18n/useTranslations';
import {
  buildInvoiceLineItemsFromReconciliation,
  calculateInvoiceTotal,
  getInvoiceCurrencyCode,
  transformLoadLineItemsToInvoiceLineItems,
} from '../../utils/invoiceLineItems';
import {
  transformLoadFormToInput,
  transformLoadLineItemsToReceivedLineItems,
} from '../../utils/load';
import { ConfirmPopoverButton } from '../_shared/ConfirmPopoverButton';

import { LoadAssignDataAction } from './LoadAssignDataAction';
import { LoadStatusTransitionMarkAsReceivedButton } from './LoadStatusTransitionMarkAsReceivedButton';
import { LoadStatusTransitionMarkAsReconciledButton } from './LoadStatusTransitionMarkAsReconciledButton';
import { LoadStatusTransitionMarkAsShippedButton } from './LoadStatusTransitionMarkAsShippedButton';

import {
  RevertVisibility,
  useRevertLoadStatus,
} from '~/modules/trade/_components/loads/hooks/useRevertLoadStatus';
import { LoadRecord } from '~/modules/trade/types/load';

type Props = {
  load?: Pick<
    LoadRecord,
    | 'status'
    | 'id'
    | 'salesOrderId'
    | 'bookingId'
    | 'bookingStatus'
    | 'loadNumber'
    | 'lineItems'
    | 'facilityId'
    | 'accountId'
    | 'shippedDate'
    | 'reviewed'
    | 'files'
    | 'invoiceId'
    | 'salesOrder'
  >;
  displayOnlyMarkAsButton?: boolean;
  popOverDirection?: 'top' | 'bottom';
  onVoid?: () => void;
  onRefetch?: () => void;
  disabled?: boolean;
  hideVoidButton?: boolean;
  skipMaterialsReconciliation?: boolean;
  displayDetailButton?: boolean;
  revertMenuVisibility?: RevertVisibility;
  revertButtonVisibility?: RevertVisibility;
};

export const LoadStatusTransition: React.FC<Props> = ({
  load,
  displayOnlyMarkAsButton = false,
  hideVoidButton = false,
  popOverDirection = 'bottom',
  onVoid,
  onRefetch,
  disabled = false,
  skipMaterialsReconciliation = false,
  displayDetailButton = false,
  revertMenuVisibility = 'hideIfVoidedOrReconciled',
  revertButtonVisibility = 'onlyIfVoidedOrReconciled',
}) => {
  const t = useTranslations();
  const toast = useToast();
  const formState = useAtomValue($materialsReconciliationForm);
  const setInvoiceId = useSetAtom($setInvoiceId);
  const tagToLineItemMap = useAtomValue($tagToLineItemMap);
  const deductionTypesData = useAtomValue($deductionTypes);

  const deductionTypesMap = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const dt of deductionTypesData?.data ?? []) {
      map.set(dt.id, dt.name);
    }
    return map;
  }, [deductionTypesData?.data]);

  const [isLoading, setIsLoading] = React.useState(false);

  const loadForm = useAtomValue($loadForm);

  const lineItems = load?.lineItems ?? [];

  const stagedLineItems = React.useMemo(
    () =>
      lineItems.filter(
        (item) => item.phase === enumSalesLoadLineItemPhase.Staged,
      ),
    [lineItems],
  );

  const requestedLineItems = React.useMemo(
    () =>
      lineItems.filter(
        (item) =>
          item.phase === enumSalesLoadLineItemPhase.Requested || !item.phase,
      ),
    [lineItems],
  );

  const shippedLineItems = React.useMemo(
    () =>
      lineItems.filter(
        (item) => item.phase === enumSalesLoadLineItemPhase.Shipped,
      ),
    [lineItems],
  );

  const lineItemsToShip = React.useMemo(() => {
    if (stagedLineItems.length > 0) {
      return stagedLineItems.map((item) => ({
        ...item,
        id: `temp-shipped-${item.id}`,
        phase: enumSalesLoadLineItemPhase.Shipped,
      }));
    }

    return requestedLineItems.map((item) => ({
      ...item,
      id: `temp-requested-to-ship-${item.id}`,
      phase: enumSalesLoadLineItemPhase.Shipped,
    }));
  }, [stagedLineItems, requestedLineItems]);

  const handleViewLoadInfo = React.useCallback(() => {
    if (!load?.id) return;
    window.open(`/trade/loads/${load.id}`, '_blank');
  }, [load?.id]);

  // Status transition mutations via atoms
  const markAsShippedAndUpdateLoad = useSetAtom($markAsShippedAndUpdateLoad);
  const markAsReceived = useSetAtom($markAsReceived);
  const markAsReconciled = useSetAtom($markAsReconciled);
  const markAsVoided = useSetAtom($markAsVoided);
  const revertStatus = useSetAtom($revertStatus);
  const createInvoice = useSetAtom($createInvoiceFromReconciliation);
  const updateInvoiceLineItems = useSetAtom($updateInvoiceLineItems);

  const getNextStatus = React.useCallback(
    (currentStatus: LoadStatus): LoadStatus | null => {
      switch (currentStatus) {
        case enumLoadStatus.Pending:
        case enumLoadStatus.Unassigned:
          return enumLoadStatus.Shipped;
        case enumLoadStatus.Shipped:
          return enumLoadStatus.Received;
        case enumLoadStatus.Received:
          return enumLoadStatus.Reconciled;
        case enumLoadStatus.Reconciled:
        case enumLoadStatus.Voided:
          return null; // No further transitions
        default:
          return null;
      }
    },
    [],
  );

  const handleVoid = React.useCallback(async () => {
    if (!load?.id) return;

    try {
      await markAsVoided({ loadId: load.id });
      toast.success(t.loadDetail.messages.voided);

      // Call the callback if provided
      if (onVoid) {
        onVoid();
      }
    } catch (error) {
      toast.error(
        `Failed to void load: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  }, [load?.id, markAsVoided, onVoid, toast]);

  const { menuItems, popovers } = useLoadDetailMenuItems(
    load as LoadRecord,
    revertMenuVisibility,
  );

  // Use shared hook for revert status/label
  const [revertStatusTarget, revertStatusLabel] = useRevertLoadStatus({
    status: load?.status,
    salesOrderId: load?.salesOrderId,
    visibility: revertButtonVisibility,
  });

  const handleRevertStatus = React.useCallback(async () => {
    if (!revertStatusTarget || !load?.id) return;
    try {
      setIsLoading(true);
      await revertStatus({
        id: load.id,
        targetStatus: revertStatusTarget as LoadStatus,
      });
      toast.success(t.loadDetail.actions.revertStatusSuccess);
    } catch (error) {
      console.error('Failed to revert load status:', error);
      toast.error(t.loadDetail.actions.revertStatusError);
    } finally {
      setIsLoading(false);
      onRefetch?.();
    }
  }, [load?.id, revertStatus, t, revertStatusTarget, toast, onRefetch]);

  const handleShipLoad = React.useCallback(
    async (
      shippedDate: CalendarDate | undefined,
      lightWeight: Weight | null,
      heavyWeight: Weight | null,
    ) => {
      if (!load?.id) return;

      setIsLoading(true);

      try {
        const input = transformLoadFormToInput({
          form: {
            ...loadForm,
            lineItems: [...lineItems, ...lineItemsToShip],
          },
          tab: 'shipped',
          tagToLineItemMap,
        });

        await markAsShippedAndUpdateLoad({
          markAsShipped: {
            loadId: load.id,
            shippedDate: toZoned(
              shippedDate ?? today(getLocalTimeZone()),
              getLocalTimeZone(),
            ).toAbsoluteString(),
            lightWeight: lightWeight,
            heavyWeight: heavyWeight,
          },
          updateLoad: {
            id: load.id,
            ...input,
          },
        });

        toast.success(t.loadStatusTransition.markAsShippedPopover.shipped);

        // Create ProForma invoice for individual loads (not in a booking)
        if (!load.bookingId && load.accountId && load.facilityId) {
          try {
            // Transform line items for invoice
            const lineItemsForInvoice = lineItemsToShip.map((li) => ({
              id: li.id,
              itemId: li.itemId ?? null,
              itemName: li.item?.name ?? 'Unknown Item',
              weights: li.weights ?? null,
              unitPrice: li.unitPrice ?? null,
              pricingUnit: li.pricingUnit ?? null,
            }));

            const invoiceLineItems = transformLoadLineItemsToInvoiceLineItems(
              lineItemsForInvoice,
              `Load #${load.loadNumber}`,
            );

            const amount = calculateInvoiceTotal(invoiceLineItems);

            if (amount > 0 && invoiceLineItems.length > 0) {
              const currencyCode = getInvoiceCurrencyCode(invoiceLineItems);
              const invoiceResult = await createInvoice({
                loadId: load.id,
                accountId: load.accountId,
                facilityId: load.facilityId,
                documentDate: (
                  shippedDate ?? today(getLocalTimeZone())
                ).toString(),
                paymentTerm:
                  load.salesOrder?.paymentTerm ?? enumPaymentTerm.Net30,
                amount,
                currencyCode,
                lineItems: invoiceLineItems,
                notes: `Invoice for Load #${load.loadNumber}`,
                status: enumInvoiceStatus.ProForma,
              });

              toast.success(
                t.loadStatusTransition.markAsShippedPopover.invoiceCreated(
                  invoiceResult.financeInvoiceCreate.documentNumber,
                ),
              );
            }
          } catch (invoiceError) {
            console.error('Failed to create invoice:', invoiceError);
            toast.error(
              t.loadStatusTransition.markAsShippedPopover.invoiceCreationFailed,
            );
          }
        }
      } finally {
        setIsLoading(false);
        onRefetch?.();
      }
    },
    [
      load,
      loadForm,
      lineItems,
      lineItemsToShip,
      tagToLineItemMap,
      markAsShippedAndUpdateLoad,
      createInvoice,
      toast,
      t.loadStatusTransition.markAsShippedPopover.shipped,
      onRefetch,
    ],
  );

  const loadId = load?.id;

  const handleReceiveLoad = React.useCallback(
    async (loadReceivedDate: CalendarDate | undefined) => {
      if (!loadId) return;

      try {
        if (!loadReceivedDate) {
          throw new Error(
            t.loadStatusTransition.markAsReceivedPopover.missingDate,
          );
        }

        setIsLoading(true);

        const receivedLineItems = transformLoadLineItemsToReceivedLineItems(
          shippedLineItems || [],
        );

        await markAsReceived({
          loadId,
          receivedDate: loadReceivedDate ?? today(getLocalTimeZone()),
          receivedLineItems,
        });

        toast.success(t.loadStatusTransition.markAsReceivedPopover.received);
        onRefetch?.();
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Unknown error');
      } finally {
        setIsLoading(false);
        onRefetch?.();
      }
    },
    [
      loadId,
      t.loadStatusTransition.markAsReceivedPopover.missingDate,
      t.loadStatusTransition.markAsReceivedPopover.received,
      shippedLineItems,
      markAsReceived,
      toast,
      onRefetch,
    ],
  );

  const handleReconcileLoad = React.useCallback(async () => {
    if (!load?.id) return;

    try {
      setIsLoading(true);

      const allLineItems = buildInvoiceLineItemsFromReconciliation({
        invoiceType: formState.invoiceType ?? 'from_shipped_materials',
        receivedLineItems: formState.receivedLineItems ?? [],
        shippedLineItems: formState.shippedLineItems ?? [],
        deductions: formState.deductions ?? [],
        freightCharges: formState.freightCharges ?? [],
        deductionTypesMap,
        loadNumber: load.loadNumber,
      });

      if (allLineItems.length > 0) {
        const amount = Math.max(0, calculateInvoiceTotal(allLineItems));
        const currencyCode = getInvoiceCurrencyCode(allLineItems);

        try {
          if (load.invoiceId) {
            await updateInvoiceLineItems({
              invoiceId: load.invoiceId,
              lineItems: allLineItems,
              amount,
              currencyCode,
              status: enumInvoiceStatus.Pending,
              documentDate: formState.invoiceDate?.toString(),
              dueDate: formState.dueDate?.toString(),
            });
            toast.success(
              t.loadStatusTransition.markAsReconciledPopover.invoiceUpdated,
            );
          } else if (load.accountId && load.facilityId) {
            const invoiceResult = await createInvoice({
              loadId: load.id,
              accountId: load.accountId,
              facilityId: load.facilityId,
              documentDate: formState.invoiceDate
                ? formState.invoiceDate.toString()
                : today(getLocalTimeZone()).toString(),
              paymentTerm:
                formState.paymentTerm ??
                load.salesOrder?.paymentTerm ??
                enumPaymentTerm.Net30,
              amount,
              currencyCode,
              lineItems: allLineItems,
              status: enumInvoiceStatus.Pending,
              sourceReferences: [
                {
                  entityId: load.id,
                  entityType: enumInvoiceEntityReferenceType.OutboundLoad,
                },
              ],
            });
            setInvoiceId(invoiceResult.financeInvoiceCreate.id);
            toast.success(
              t.loadStatusTransition.markAsReconciledPopover.invoiceCreated(
                invoiceResult.financeInvoiceCreate.documentNumber,
              ),
            );
          }
        } catch (invoiceError) {
          console.error('Failed to create/update invoice:', invoiceError);
          toast.error(
            t.loadStatusTransition.markAsReconciledPopover
              .invoiceOperationFailed,
          );
        }
      }

      await markAsReconciled({ id: load.id });

      if (load.invoiceId) {
        setInvoiceId(load.invoiceId);
      }

      toast.success(t.loadStatusTransition.markAsReconciledPopover.reconciled);
    } catch (error) {
      toast.error(
        t.loadStatusTransition.markAsReconciledPopover.reconcileFailed(
          error instanceof Error ? error.message : 'Unknown error',
        ),
      );
    } finally {
      setIsLoading(false);
      onRefetch?.();
    }
  }, [
    load?.id,
    load?.invoiceId,
    load?.loadNumber,
    load?.accountId,
    load?.facilityId,
    load?.salesOrder?.paymentTerm,
    formState.invoiceType,
    formState.receivedLineItems,
    formState.shippedLineItems,
    formState.deductions,
    formState.freightCharges,
    formState.invoiceDate,
    formState.dueDate,
    formState.paymentTerm,
    deductionTypesMap,
    markAsReconciled,
    createInvoice,
    updateInvoiceLineItems,
    toast,
    t.loadStatusTransition.markAsReconciledPopover.reconciled,
    setInvoiceId,
    onRefetch,
  ]);

  if (!load) {
    return null;
  }

  const nextStatus = getNextStatus(load.status);
  if (!nextStatus && displayOnlyMarkAsButton) {
    return null;
  }

  const loadSalesOrderIsAssigned = !!load?.salesOrderId;
  const loadBookingIsAssigned = !!load?.bookingId;
  const loadCanBeShipped =
    nextStatus === enumLoadStatus.Shipped &&
    loadSalesOrderIsAssigned &&
    loadBookingIsAssigned;

  const bookingIsShipped =
    !load.bookingId ||
    load.bookingStatus === enumBookingStatus.Shipped ||
    load.bookingStatus === enumBookingStatus.Completed;

  return (
    <div className="@LoadStatusTransition contents">
      <div className="flex items-center gap-2">
        {!displayOnlyMarkAsButton &&
          loadSalesOrderIsAssigned &&
          loadBookingIsAssigned && (
            <Menu
              trigger={<Button icon={<MoreHorizontal />} variant="outline" />}
              items={menuItems}
            />
          )}
        {displayDetailButton && (
          <Button variant="outline" onPress={handleViewLoadInfo}>
            {t.loadDetail.actions.viewLoadInfo}
          </Button>
        )}
        {!displayOnlyMarkAsButton &&
          !hideVoidButton &&
          load.status !== enumLoadStatus.Voided &&
          load.status !== enumLoadStatus.Reconciled && (
            <ConfirmPopoverButton
              onConfirm={handleVoid}
              message={t.loadDetail.confirmVoidMessage}
              confirmLabel={t.confirmPopoverButton.confirm}
              cancelLabel={t.confirmPopoverButton.cancel}
              buttonProps={{ variant: 'outline', color: 'failure' }}
            >
              {t.loadDetail.actions.void}
            </ConfirmPopoverButton>
          )}
        {/* Render revert button as main action if available */}
        {revertStatusTarget && revertStatusLabel && (
          <Button
            variant="outline"
            onPress={handleRevertStatus}
            isDisabled={disabled || isLoading}
          >
            {revertStatusLabel}
          </Button>
        )}
        {(!loadSalesOrderIsAssigned || !loadBookingIsAssigned) && (
          <LoadAssignDataAction
            loadId={load.id}
            hasBookingId={loadBookingIsAssigned}
            hasSalesOrderId={loadSalesOrderIsAssigned}
          />
        )}
        {loadCanBeShipped && (
          <LoadStatusTransitionMarkAsShippedButton
            onMarkAsShipped={handleShipLoad}
            load={load}
            isLoading={isLoading}
            shippedDate={load.shippedDate ?? undefined}
            popoverPlacement={popOverDirection}
          />
        )}
        {nextStatus === enumLoadStatus.Received && (
          <Tooltip
            tip={
              t.loadStatusTransition.markAsReceivedPopover.bookingMustBeShipped
            }
            isDisabled={bookingIsShipped}
            wrapWithButton={false}
          >
            <LoadStatusTransitionMarkAsReceivedButton
              onMarkAsReceived={handleReceiveLoad}
              isLoading={isLoading}
              isDisabled={!bookingIsShipped}
              popoverPlacement={popOverDirection}
            />
          </Tooltip>
        )}
        {nextStatus === enumLoadStatus.Reconciled && load.reviewed && (
          <LoadStatusTransitionMarkAsReconciledButton
            loadId={load.id}
            loadNumber={load.loadNumber}
            onMarkAsReconciled={handleReconcileLoad}
            isLoading={isLoading}
            skipMaterialsReconciliation={skipMaterialsReconciliation}
          />
        )}
      </div>
      {popovers}
    </div>
  );
};
